import Uppy from '@uppy/core'
import Tus from '@uppy/tus'
// We're not using GoldenRetriever to avoid storage quota issues

import { ALLOWED_META_FIELDS } from '@features/subject/utils'
import { SESSION_STORAGE_KEY } from '@common/constants/api'
import { userManager } from '@auth/oidc.config'

let uppyInstance: Uppy | null = null

// Token caching to reduce refresh checks
interface TokenCache {
  token: string
  expiresAt: number
  checkedAt: number
}

let tokenCache: TokenCache | null = null
// Only check token freshness every 30 seconds at most
const TOKEN_CHECK_INTERVAL = 30 * 1000 // 30 seconds

/**
 * Gets the latest access token from session storage
 * @returns The current access token
 */
function getLatestToken(): string {
  const userData = sessionStorage.getItem(SESSION_STORAGE_KEY)
  if (!userData) {
    throw new Error('No user data found in Session Storage')
  }

  const userJson = JSON.parse(userData)
  const accessToken = userJson['access_token']

  if (!accessToken) {
    throw new Error('No access token found in session storage')
  }

  return accessToken
}

/**
 * Ensures we have a fresh token for API requests
 * @param refreshThresholdPercentage Refresh token when this percentage of its lifetime remains
 * @returns A fresh access token
 */
async function ensureFreshToken(refreshThresholdPercentage = 0.15): Promise<string> {
  try {
    // Use cached token if we recently checked and it's not near expiration
    const currentTime = Date.now()
    if (
      tokenCache &&
      currentTime < tokenCache.checkedAt + TOKEN_CHECK_INTERVAL &&
      currentTime / 1000 < tokenCache.expiresAt - tokenCache.expiresAt * refreshThresholdPercentage
    ) {
      console.log('Using cached token, last checked:', new Date(tokenCache.checkedAt).toISOString())
      return tokenCache.token
    }

    // Check current token expiry
    const user = await userManager.getUser()
    if (!user) {
      throw new Error('User is not logged in')
    }

    // Calculate token time-to-live in seconds
    const expiresAt = user.expires_at || 0
    const currentTimeSeconds = Math.floor(currentTime / 1000)
    const tokenTTL = expiresAt - currentTimeSeconds

    // If token is already expired, refresh immediately
    if (tokenTTL <= 0) {
      console.log('Token is already expired, refreshing immediately')
      await userManager.signinSilent()
      const freshToken = getLatestToken()

      // Update cache with new token
      const newUser = await userManager.getUser()
      if (newUser && newUser.expires_at) {
        tokenCache = {
          token: freshToken,
          expiresAt: newUser.expires_at,
          checkedAt: currentTime,
        }
      }

      return freshToken
    }

    // Calculate refresh threshold as a percentage of total token lifetime
    // For example, with a 1-hour token and 15% threshold, refresh when 9 minutes remain
    const refreshThresholdSeconds = tokenTTL * refreshThresholdPercentage

    // Check if we're within the refresh threshold
    if (tokenTTL <= refreshThresholdSeconds) {
      console.log(`Token expires in ${tokenTTL}s, refreshing with ${refreshThresholdPercentage * 100}% threshold`)
      await userManager.signinSilent()
      const freshToken = getLatestToken()

      // Update cache with new token
      const newUser = await userManager.getUser()
      if (newUser && newUser.expires_at) {
        tokenCache = {
          token: freshToken,
          expiresAt: newUser.expires_at,
          checkedAt: currentTime,
        }
      }

      return freshToken
    } else {
      console.log(`Token still valid for ${tokenTTL}s, no refresh needed (threshold: ${refreshThresholdSeconds}s)`)
      // Update cache with current token
      const currentToken = getLatestToken()
      tokenCache = {
        token: currentToken,
        expiresAt: expiresAt,
        checkedAt: currentTime,
      }

      return currentToken
    }
  } catch (error) {
    console.error('Failed to refresh token:', error)
    // Return the current token even if refresh failed
    return getLatestToken()
  }
}

/**
 * Clear existing Uppy storage to prevent quota issues
 */
function clearUppyStorage() {
  try {
    // Clear localStorage items related to Uppy
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.includes('uppy')) {
        console.log('Clearing Uppy localStorage item:', key)
        localStorage.removeItem(key)
      }
    }
  } catch (error) {
    console.error('Error clearing localStorage:', error)
  }
}

// Track files that received 404 errors to handle them specially
const filesWithNotFoundErrors = new Set<string>()

export const createUppy = () => {
  if (uppyInstance) return uppyInstance

  // Clear any existing uppy storage to prevent quota errors
  clearUppyStorage()

  // Reset tracking state when creating a new instance
  filesWithNotFoundErrors.clear()

  // Create base Uppy instance with size restrictions
  uppyInstance = new Uppy({
    debug: true, // Enable all Uppy logs
    logger: {
      debug: (...args: unknown[]) => console.log('[Uppy Debug]', ...args),
      warn: (...args: unknown[]) => console.warn('[Uppy Warning]', ...args),
      error: (...args: unknown[]) => console.error('[Uppy Error]', ...args),
    },
    autoProceed: false,
  }).use(Tus, {
    endpoint: `${import.meta.env.VITE_API_URL}/timepoint/files`,
    allowedMetaFields: ALLOWED_META_FIELDS,
    limit: 10, // Maximum concurrent uploads, reduced to avoid overwhelming the server
    chunkSize: 1 * 1024 * 1024, // 5MB chunks for better handling

    // Increase retries and delays for robustness
    retryDelays: [0, 1000, 3000, 5000, 10000, 15000, 30000], // More retries with longer delays

    // Custom retry handler for 404s and other errors
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onShouldRetry: (err: any, attemptNumber: number) => {
      console.log(`[Uppy TUS] Request error (attempt ${attemptNumber}):`, err?.status, err?.message)
      console.log('[Uppy TUS] Error details:', JSON.stringify(err, null, 2))

      // Extract file ID from the URL if available
      const urlParts = err?.request?.url?.split('/') || []
      const fileId = urlParts[urlParts.length - 1]?.split('?')[0]

      // Special handling for 404 errors during HEAD requests (resumable upload checks)
      if (err?.status === 404 && fileId) {
        console.log(`[Uppy TUS] 404 Not Found for file ID: ${fileId}`)

        // Track this file for special handling
        filesWithNotFoundErrors.add(fileId)

        // Check if this was a HEAD request (checking upload status)
        const method = err?.request?.method
        if (method === 'HEAD') {
          console.log(`[Uppy TUS] This was a HEAD request - we should try creating a new upload`)

          // For 404 on HEAD requests (checking existing upload), we should retry
          // TUS will automatically create a new upload on the next attempt
          return true
        }
      }

      // Always retry 500 errors and network errors
      if (!err?.status || err?.status >= 500) {
        console.log(`[Uppy TUS] Will retry after ${err?.status || 'network error'}`)
        return true
      }

      // Default Uppy behavior for other status codes
      return [408, 409, 423, 429, 502, 503, 504].includes(err?.status || 0)
    },

    // Don't set initial headers - will be set in onBeforeRequest
    headers: () => {
      return {}
    },

    // Special handling for requests to improve 404 handling
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onBeforeRequest: async (req, _file) => {
      try {
        const method = req.getMethod?.() || 'unknown method'
        console.log('[Uppy TUS] Preparing request:', method)

        // Get file ID from the URL if possible
        const urlParts = req.getURL?.()?.split('/') || []
        const fileId = urlParts[urlParts.length - 1]?.split('?')[0]

        // Special handling for files that previously had 404 errors
        if (fileId && filesWithNotFoundErrors.has(fileId) && method === 'HEAD') {
          console.log(`[Uppy TUS] This file previously had 404 errors: ${fileId}`)
          // We could add special handling here if needed
        }

        const freshToken = await ensureFreshToken()

        // Only log if there's an existing Authorization header (potential issue)
        const currentHeader = req.getHeader('Authorization')
        if (currentHeader) {
          console.warn('Existing Authorization header detected:', currentHeader)
        }

        // Set a single Authorization header with our fresh token
        // This replaces any existing Authorization header
        req.setHeader('Authorization', `Bearer ${freshToken}`)

        // Properly match the expected return type (void)
      } catch (error) {
        console.error('Error in onBeforeRequest:', error)
      }
    },
  })

  // Add listeners for better debugging
  uppyInstance.on('upload-error', (file, error, response) => {
    if (file) {
      console.error(
        `[Uppy] Upload error for file ${file.name}:`,
        error?.message || 'Unknown error',
        'Response:',
        response,
      )

      // For 404 errors, we might want to force a full restart of this file
      if (response && response.status === 404) {
        console.log(`[Uppy] 404 error for ${file.name}, may need to restart upload from beginning`)

        // In some cases, we might want to force a new upload by clearing the existing uploadURL
        // This would make TUS create a new upload instead of trying to resume
        if (file.tus && file.tus.uploadUrl) {
          console.log(`[Uppy] Clearing uploadURL to force a fresh upload attempt`)
          // The next line is commented out as this would require modifying Uppy internals
          // file.tus.uploadUrl = null;
        }
      }
    } else {
      console.error('[Uppy] Upload error:', error?.message || 'Unknown error', 'Response:', response)
    }
  })

  uppyInstance.on('error', (error) => {
    console.error('[Uppy] General error:', error?.message || 'Unknown error')
  })

  uppyInstance.on('retry-all', () => {
    console.log('[Uppy] Retrying all failed uploads')
  })

  uppyInstance.on('upload', () => {
    console.log('[Uppy] Upload started')
  })

  // Custom event handler to log when individual files are retried
  uppyInstance.on('upload-retry', (fileID) => {
    console.log(`[Uppy] Retrying upload for file ID: ${fileID}`)
  })

  return uppyInstance
}

// Add a function to reset the instance if needed
export const resetUppy = () => {
  if (uppyInstance) {
    // Cancel any ongoing uploads
    uppyInstance.cancelAll()
    uppyInstance = null

    // Clear any Uppy-related storage
    clearUppyStorage()

    // Reset the tracking set
    filesWithNotFoundErrors.clear()
  }
}
